import { useState, useEffect, useCallback } from 'react';
import { getAllSessions } from '../utils/db';
import { getToday, getDaysAgo } from '../utils/timeUtils';

/**
 * Hook that provides session data, garden plants, and statistics.
 */
export function useSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSessions = useCallback(async () => {
    try {
      const all = await getAllSessions();
      setSessions(all.sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || '')));
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Completed sessions (plants in the garden)
  const completedSessions = sessions.filter((s) => s.completed && !s.killed);
  const killedSessions = sessions.filter((s) => s.killed);

  // Today's sessions
  const today = getToday();
  const todaySessions = completedSessions.filter((s) => s.date === today);
  const todayKilled = killedSessions.filter((s) => s.date === today);

  // This week's sessions
  const weekAgo = getDaysAgo(7);
  const weekSessions = completedSessions.filter((s) => s.date >= weekAgo);

  // Total stats
  const totalMinutes = completedSessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
  const totalPlants = completedSessions.length;
  const totalXP = completedSessions.reduce((sum, s) => sum + (s.xpEarned || 0), 0);

  // Today stats
  const todayMinutes = todaySessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);

  // Week stats
  const weekMinutes = weekSessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);

  // Completion rate
  const totalAttempts = completedSessions.length + killedSessions.length;
  const completionRate = totalAttempts > 0 ? Math.round((completedSessions.length / totalAttempts) * 100) : 0;

  // Garden data: group completed sessions by date for the grid
  const gardenByDate = {};
  completedSessions.forEach((s) => {
    if (!gardenByDate[s.date]) gardenByDate[s.date] = [];
    gardenByDate[s.date].push(s);
  });

  // Daily focus minutes for the last 7 days (for mini chart)
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = getDaysAgo(6 - i);
    const daySessions = completedSessions.filter((s) => s.date === date);
    const minutes = daySessions.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
    return { date, minutes, sessions: daySessions.length };
  });

  // Tag distribution
  const tagDistribution = {};
  completedSessions.forEach((s) => {
    const tag = s.tag || 'work';
    tagDistribution[tag] = (tagDistribution[tag] || 0) + (s.durationMinutes || 0);
  });

  return {
    sessions,
    completedSessions,
    killedSessions,
    loading,
    refresh: loadSessions,
    stats: {
      totalMinutes,
      totalPlants,
      totalXP,
      todayMinutes,
      todaySessions: todaySessions.length,
      todayKilled: todayKilled.length,
      weekMinutes,
      weekSessions: weekSessions.length,
      completionRate,
      last7Days,
      tagDistribution,
    },
    gardenByDate,
  };
}
